import { nanoid } from 'nanoid'
import { redact } from '../utils/redact.mjs'
import stringify from 'fast-json-stable-stringify'
/**
 * A generic, secure, persistent filter that can be extended for specific types
 * of filters.  This class should not be instantiated directly.  It provides
 * the basic structure and functionality for filters, including:
 *
 * - type: The type of filter (e.g., 'domain', 'regex', 'exact', etc.)
 * - id: A unique identifier for the filter
 * - label: A human-readable label for the filter
 * - value: The value used for filtering (e.g., domain, prefix, exact URL, etc.)
 * - secure: A boolean indicating if the value should be obscured (hashed) ***
 * - inclusive: A boolean indicating if this is an inclusionary filter
 *
 * Subclasses must implement the `matches(target)` method to define how to match
 * a target against the filter.
 *
 * The `toJSON()` method provides a way to serialize the filter for storage or
 * transmission, ensuring that secure values are appropriately handled.
 *
 * *** Secure values are obscured using a hashing function to protect sensitive
 * information.  If the user is filtering out a URL that they don't want to be
 * stored, the secure flag should be set to true.  This will mean whatever value
 * the filter depends on will be stored as a hash and the filter will need to
 * employ strict hash equality matching to be effective.  Non-secure filters
 * may use partial matching techniques like "starts with" or regex.
 */

export class AbstractPersistentFilter {
  constructor(type, { id, value, inclusive, secure }) {
    if (!type) throw new Error('AbstractPersistentFilter requires a type')
    if (!value) throw new Error('AbstractPersistentFilter requires a value')
    this.type = type // e.g., 'domain', 'regex', 'exact', etc.
    this.id = id || nanoid()
    this.value = value // e.g., domain, prefix, exact URL, etc.
    this.secure = !!secure // obscure sensitive values with hashes
    this.inclusive = !!inclusive // whether this is an inclusionary filter
  }

  /**
   * Matches a target against this filter, returning true if it matches, regardless
   * of whether the filter is inclusive or exclusive.
   *
   * @param target
   */
  matches(target) {
    throw new Error('matches(target) not implemented')
  }

  explain(target) {
    throw new Error('explain(target) not implemented')
  }

  get simpleLabel() {
    throw new Error('simpleLabel() not implemented')
  }

  get label() {
    return this.simpleLabel
  }

  toJson() {
    return {
      type: this.type,
      value: this.value,
      secure: !!this.secure,
      inclusive: !!this.inclusive,
    }
  }

  hashCode() {
    return redact(stringify(this.toJson()))
  }
  equals(other) {
    if (!(other instanceof AbstractPersistentFilter)) return false
    return this.hashCode() === other.hashCode()
  }
  _normalize(inputValue) {
    return this.secure ? redact(inputValue) : inputValue
  }
}
